import { useState, useEffect } from 'react'
import {
  Box, Typography, CircularProgress, Button,
  Card, CardContent,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { backendClient } from '../../../../api/backend'
import { useSnackbar } from 'notistack'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import SubscriptionDetails from './SubscriptionDetails'
import UserSpecificMessage from './UserSpecificMessage'

const BillingInfo = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const user = useSelector((state) => state.auth.user.user)
  const { enqueueSnackbar } = useSnackbar()

  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openingPortal, setOpeningPortal] = useState(false)

  const isBillingAdmin = user?.roles?.includes('billing_admin')

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const sub = await backendClient.getSubscription()
      setSubscription(sub)
    } catch (err) {
      console.error('Failed to fetch billing data:', err)
      setError(err.message || 'Failed to load billing information')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [user]) // eslint-disable-line

  const formatDate = (dateString) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    })
  }

  const handleCancelSubscription = async () => {
    try {
      setLoading(true)
      await backendClient.cancelSubscription()
      setSubscription((prev) => prev ? { ...prev, status: 'cancelled' } : prev)
      enqueueSnackbar(t('subscription_cancelled_success') || 'Subscription cancelled', { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(t('subscription_cancellation_failed') || 'Cancellation failed', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleManagePaymentMethod = async () => {
    setOpeningPortal(true)
    try {
      const data = await backendClient.getBillingPortalUrl()
      window.open(data.portal_url, '_blank')
    } catch (err) {
      enqueueSnackbar(t('billing_portal_error'), { variant: 'error' })
    } finally {
      setOpeningPortal(false)
    }
  }

  // ── Loading ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
      </Box>
    )
  }

  // ── Error ──
  if (error) {
    return (
      <Card elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${theme.palette.error.main}`, mb: 4 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }} />
          <Typography color="error" variant="h6" sx={{ mb: 2 }}>{error}</Typography>
          <Button
            variant="contained" disableElevation
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
            onClick={() => fetchData()}
          >
            {t('retry') || 'Retry'}
          </Button>
        </CardContent>
      </Card>
    )
  }

  // ── Non-billing-admin (team members) ──
  if (!isBillingAdmin) {
    return <UserSpecificMessage />
  }

  return (
    <Box>
      {/* Subscription Details */}
      <Card elevation={0} sx={{ mb: 4, borderRadius: '16px', border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SubscriptionDetails
            subscription={subscription}
            formatDate={formatDate}
            handleCancelSubscription={handleCancelSubscription}
            theme={theme}
          />
        </CardContent>
      </Card>

      {/* Payment, Billing & Invoices — managed on Stripe */}
      <Card elevation={0} sx={{ mb: 4, borderRadius: '16px', border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, flex: 1 }}>
              {t('manage_billing_description')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              disabled={openingPortal}
              onClick={handleManagePaymentMethod}
              sx={{
                borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                borderColor: theme.palette.divider, color: theme.palette.text.primary,
                '&:hover': { borderColor: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
              }}
            >
              {openingPortal ? t('opening') : t('manage_billing')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default BillingInfo
